import { io, Socket } from 'socket.io-client'
import type { RealtimeUpdate, Notification } from '@/types'
import type { GlobalAnnouncement } from '@/hooks/useGlobalAnnouncements'

class SocketClient {
  private socket: Socket | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5

  connect(): void {
    if (this.socket?.connected) {
      return
    }

    // Get JWT token from localStorage (correct key is 'accessToken')
    const token = localStorage.getItem('accessToken')
    // Use current origin for socket connection - works in both local dev and production
    const socketUrl = window.location.origin

    if (!token) {
      console.warn('No access token found - cannot connect to socket')
      return
    }

    this.socket = io(socketUrl, {
      // Read the token at every (re)connection attempt. A token captured once
      // goes stale after the HTTP layer silently refreshes it, so reconnects
      // (Cloud Run instance recycling, laptop sleep) presented an expired JWT
      // and were rejected — which, combined with the old handler below wiping
      // the session, logged users out mid-page seconds after it rendered.
      auth: (cb) => cb({ token: localStorage.getItem('accessToken') || token }),
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
    })

    this.socket.on('connect', () => {
      console.log('Socket connected and authenticated')
      this.reconnectAttempts = 0
    })

    this.socket.onAny((event, ...args) => {
      console.log(`[Socket] Event received: "${event}"`, args)
    })

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason)
    })

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message)
      this.reconnectAttempts++

      // Handle authentication failures. NEVER tear down the session from the
      // socket layer — the HTTP client owns session lifecycle (it silently
      // refreshes tokens and redirects to login only when the refresh itself
      // fails). Wiping tokens and hard-redirecting here logged users out of a
      // fully working page whenever a reconnect presented a stale token.
      // Real-time updates simply stay off until the next connect.
      if (
        error.message === 'Authentication required' ||
        error.message === 'Token expired' ||
        error.message === 'Invalid token' ||
        error.message === 'Authentication failed'
      ) {
        console.warn('Socket authentication failed — real-time updates disabled for this session')
        this.socket?.close()
        return
      }

      // Stop trying after max attempts for other errors
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnection attempts reached - closing socket')
        this.socket?.close()
      }
    })
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }
  }

  // Listen for entity updates
  onEntityUpdate(callback: (update: RealtimeUpdate) => void): void {
    this.socket?.on('entity:update', callback)
  }

  offEntityUpdate(callback: (update: RealtimeUpdate) => void): void {
    this.socket?.off('entity:update', callback)
  }

  // Listen for notifications
  onNotification(callback: (notification: Notification) => void): void {
    this.socket?.on('notification', callback)
  }

  offNotification(callback: (notification: Notification) => void): void {
    this.socket?.off('notification', callback)
  }

  // Listen for global announcements (e.g., Closed Won celebrations)
  onAnnouncement(callback: (announcement: GlobalAnnouncement) => void): void {
    this.socket?.on('announcement', callback)
  }

  offAnnouncement(callback: (announcement: GlobalAnnouncement) => void): void {
    this.socket?.off('announcement', callback)
  }

  onMaintenance(callback: (data: { enabled: boolean; message: string }) => void): void {
    this.socket?.on('maintenance:changed', callback)
  }

  offMaintenance(callback: (data: { enabled: boolean; message: string }) => void): void {
    this.socket?.off('maintenance:changed', callback)
  }

  // Listen for content linked to entities (e.g., attachments copied to order)
  /**
   * Contract review changes.
   *
   * Written because tickets did not. `routes/tickets.js` emits eleven `ticket:*` events and
   * nothing in the frontend has ever listened for any of them, so a ticket changed in one tab
   * never appears in another. Emitting without a listener is the same as not emitting.
   */
  onContractReviewChange(callback: () => void): void {
    this.socket?.on('contract_review:created', callback)
    this.socket?.on('contract_review:updated', callback)
    this.socket?.on('contract_review:comment_added', callback)
    this.socket?.on('contract_review:deleted', callback)
  }

  offContractReviewChange(callback: () => void): void {
    this.socket?.off('contract_review:created', callback)
    this.socket?.off('contract_review:updated', callback)
    this.socket?.off('contract_review:comment_added', callback)
    this.socket?.off('contract_review:deleted', callback)
  }

  onContentLinked(callback: (data: { linked_entity_id: string }) => void): void {
    this.socket?.on('content:linked', callback)
  }

  offContentLinked(callback: (data: { linked_entity_id: string }) => void): void {
    this.socket?.off('content:linked', callback)
  }

  // Join a room (e.g., for specific account or opportunity)
  joinRoom(room: string): void {
    this.socket?.emit('join:room', room)
  }

  leaveRoom(room: string): void {
    this.socket?.emit('leave:room', room)
  }

  // Send typing indicator
  sendTyping(room: string, isTyping: boolean): void {
    this.socket?.emit('typing', { room, isTyping })
  }

  onTyping(callback: (data: { user: string; isTyping: boolean }) => void): void {
    this.socket?.on('user:typing', callback)
  }

  offTyping(callback: (data: { user: string; isTyping: boolean }) => void): void {
    this.socket?.off('user:typing', callback)
  }

  // Get connection status
  isConnected(): boolean {
    return this.socket?.connected ?? false
  }

  // Note session event listeners
  onNoteSessionCreated(callback: (data: { session: any }) => void): void {
    this.socket?.on('note_session:created', callback)
  }

  offNoteSessionCreated(callback: (data: { session: any }) => void): void {
    this.socket?.off('note_session:created', callback)
  }

  onNoteSessionUpdated(callback: (data: { session: any }) => void): void {
    this.socket?.on('note_session:updated', callback)
  }

  offNoteSessionUpdated(callback: (data: { session: any }) => void): void {
    this.socket?.off('note_session:updated', callback)
  }

  onNoteSessionAutosaved(
    callback: (data: { id: string; last_modified_date: string }) => void
  ): void {
    this.socket?.on('note_session:autosaved', callback)
  }

  offNoteSessionAutosaved(
    callback: (data: { id: string; last_modified_date: string }) => void
  ): void {
    this.socket?.off('note_session:autosaved', callback)
  }

  onNoteSessionCompleted(callback: (data: { session: any }) => void): void {
    this.socket?.on('note_session:completed', callback)
  }

  offNoteSessionCompleted(callback: (data: { session: any }) => void): void {
    this.socket?.off('note_session:completed', callback)
  }

  onNoteSessionDeleted(callback: (data: { id: string }) => void): void {
    this.socket?.on('note_session:deleted', callback)
  }

  offNoteSessionDeleted(callback: (data: { id: string }) => void): void {
    this.socket?.off('note_session:deleted', callback)
  }
}

export const socketClient = new SocketClient()
